import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface AddressComponents {
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  country?: string;
}

interface UseGooglePlacesAutocompleteOptions {
  debounceMs?: number;
  minLength?: number;
}

export const useGooglePlacesAutocomplete = (options: UseGooglePlacesAutocompleteOptions = {}) => {
  const { debounceMs = 300, minLength = 3 } = options;
  
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<AddressComponents | null>(null);
  const [selectedPlaceDescription, setSelectedPlaceDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Clear selected place if input changes from selected description
  useEffect(() => {
    if (selectedPlace && selectedPlaceDescription && inputValue !== selectedPlaceDescription) {
      setSelectedPlace(null);
      setSelectedPlaceDescription('');
    }
  }, [inputValue, selectedPlace, selectedPlaceDescription]);

  // Debounce function
  useEffect(() => {
    if (!inputValue || inputValue.length < minLength) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error: functionError } = await supabase.functions.invoke('google-places-autocomplete', {
          body: {
            action: 'autocomplete',
            input: inputValue,
          },
        });

        if (functionError) {
          throw new Error(functionError.message || 'Failed to fetch address suggestions');
        }

        if (!data) {
          throw new Error('No data returned from API');
        }
        
        if (data.status === 'OK' || data.status === 'ZERO_RESULTS') {
          setSuggestions(
            data.predictions?.map((prediction: any) => ({
              place_id: prediction.place_id,
              description: prediction.description,
              structured_formatting: prediction.structured_formatting,
            })) || []
          );
          setError(null);
        } else if (data.status === 'REQUEST_DENIED') {
          setError('Google Places API key is invalid or not configured');
          setSuggestions([]);
        } else {
          setError(`API Error: ${data.status}`);
          setSuggestions([]);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch address suggestions');
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [inputValue, debounceMs, minLength]);

  // Fetch place details and parse address components
  const selectPlace = useCallback(async (placeId: string, description: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('google-places-autocomplete', {
        body: {
          action: 'details',
          place_id: placeId,
          fields: 'address_components,formatted_address',
        },
      });

      if (functionError) {
        throw new Error(functionError.message || 'Failed to fetch place details');
      }

      if (!data) {
        throw new Error('No data returned from API');
      }

      if (data.status !== 'OK') {
        throw new Error(`API Error: ${data.status}`);
      }

      const place = data.result;
      const components = parseAddressComponents(place.address_components, place.formatted_address);
      
      setSelectedPlace(components);
      setSelectedPlaceDescription(description);
      setInputValue(description);
      setSuggestions([]);
      
      return components;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch place details');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedPlace(null);
    setSelectedPlaceDescription('');
    setInputValue('');
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    selectedPlace,
    selectedPlaceDescription,
    error,
    selectPlace,
    clearSelection,
  };
};

// Parse Google Places API address components into our format
function parseAddressComponents(
  addressComponents: any[],
  formattedAddress: string
): AddressComponents {
  const components: AddressComponents = {
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  };

  if (!addressComponents || !Array.isArray(addressComponents)) {
    // Fallback: use formatted address as street address
    components.street_address = formattedAddress || '';
    return components;
  }

  for (const component of addressComponents) {
    const types = component.types || [];

    if (types.includes('street_number')) {
      components.street_address = component.long_name + ' ';
    } else if (types.includes('route')) {
      components.street_address += component.long_name;
    } else if (types.includes('locality')) {
      components.city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      components.state = component.short_name; // Use short_name for state abbreviation
    } else if (types.includes('postal_code')) {
      components.zip_code = component.long_name;
    } else if (types.includes('country')) {
      components.country = component.short_name;
    }
  }

  // Clean up street address (remove trailing space if no route)
  components.street_address = components.street_address.trim();

  // If we don't have a proper street address, use formatted address
  if (!components.street_address) {
    components.street_address = formattedAddress || '';
  }

  return components;
}
